import { motion } from 'framer-motion';
import {
  X, Calendar as CalendarIcon, Phone, Mail, Clock, CreditCard,
  User, AlertTriangle, ExternalLink, Check, XCircle, Ban,
  MessageCircle, CalendarPlus, FileText, Copy, MapPin,
  Timer, Stethoscope, Receipt, ChevronRight, ChevronDown,
  Undo2, Banknote, Send, ArrowDownLeft, ArrowUpRight, Gift,
  ClipboardList
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useAdmin } from '@/contexts/AdminContext';
import { useBookingDraft } from '@/contexts/BookingDraftContext';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { format, formatDistanceToNow, isPast, isToday, isTomorrow } from 'date-fns';
import { AnimatePresence } from 'framer-motion';
import TransactionDetailPanel from '@/components/admin/TransactionDetailPanel';

// ── Constants ──────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  confirmed: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500', label: 'Confirmed' },
  pending:   { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   dot: 'bg-amber-500',   label: 'Pending' },
  cancelled: { bg: 'bg-red-50',     text: 'text-red-600',     border: 'border-red-200',     dot: 'bg-red-400',     label: 'Cancelled' },
  completed: { bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200',    dot: 'bg-blue-500',    label: 'Completed' },
  no_show:   { bg: 'bg-red-100',    text: 'text-red-800',     border: 'border-red-300',     dot: 'bg-red-600',     label: 'No Show' },
};

const PAYMENT_CONFIG = {
  paid:         { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', label: 'Paid' },
  unpaid:       { bg: 'bg-rose-50',    text: 'text-rose-700',    border: 'border-rose-200',    label: 'Unpaid' },
  partially_paid: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'Partial' },
  overpaid:     { bg: 'bg-violet-50',  text: 'text-violet-700',  border: 'border-violet-200',  label: 'Overpaid' },
  refunded:     { bg: 'bg-violet-50',  text: 'text-violet-700',  border: 'border-violet-200',  label: 'Refunded' },
  not_required: { bg: 'bg-stone-50',   text: 'text-stone-600',   border: 'border-stone-200',   label: 'N/A' },
};

const TX_TYPE_CONFIG = {
  payment:    { icon: ArrowDownLeft, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Payment' },
  refund:     { icon: ArrowUpRight, color: 'text-red-600', bg: 'bg-red-50', label: 'Refund' },
  adjustment: { icon: Receipt, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Adjustment' },
  void:       { icon: Ban, color: 'text-stone-400', bg: 'bg-stone-100', label: 'Void' },
  credit:     { icon: Gift, color: 'text-violet-600', bg: 'bg-violet-50', label: 'Credit' },
};

const SITE_NAMES = {
  specialist_dental_london: 'Specialist Dental London',
  shihab_romeed: 'Shihab Romeed',
  london_dental_academy: 'London Dental Academy',
};

const TRANSITIONS = {
  pending: ['confirmed', 'cancelled'],
  confirmed: ['completed', 'cancelled', 'no_show'],
  completed: [], cancelled: [], no_show: [],
};

// ── Main Component ─────────────────────────────────────────────────────────

export default function BookingDetail({ bookingId, onClose, onUpdate }) {
  const { apiFetch } = useAdmin();
  const { createDraft } = useBookingDraft();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [activeFinanceForm, setActiveFinanceForm] = useState(null); // 'payment' | 'refund' | 'charge' | null
  const [showTransactions, setShowTransactions] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  const [services, setServices] = useState([]);

  useEffect(() => {
    if (booking?.siteKey) {
      apiFetch(`/services?siteKey=${booking.siteKey}`)
        .then(r => r.json())
        .then(d => setServices(d.services || []))
        .catch(err => console.error('Failed to fetch services:', err));
    }
  }, [booking?.siteKey, apiFetch]);

  const handleServiceChange = async (e) => {
    const newServiceId = e.target.value;
    if (newServiceId === booking.serviceId) return;
    
    const newSvc = services.find(s => s.id === newServiceId);
    let updateCharge = false;
    if (newSvc?.price !== undefined) {
      updateCharge = window.confirm(`Update charge to the new service's default amount (£${newSvc.price})?`);
    }

    setActionLoading('service');
    try {
      const res = await apiFetch(`/bookings/${booking.id}`, {
        method: 'PUT',
        body: { serviceId: newServiceId }
      });
      if (!res.ok) throw new Error('Failed to update service');

      if (updateCharge) {
        await apiFetch('/payments/charge', { 
          method: 'POST', 
          body: { bookingId: booking.id, amountCharged: newSvc.price, reason: 'Service changed' } 
        });
      }
      
      toast.success('Service updated');
      fetchBooking();
      onUpdate?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  // Fetch booking
  const fetchBooking = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await apiFetch(`/bookings/${bookingId}`);
      if (!res.ok) throw new Error('Failed to fetch booking');
      const data = await res.json();
      setBooking(data.booking);
    } catch (err) {
      toast.error(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [bookingId, apiFetch]);

  useEffect(() => { fetchBooking(); }, [fetchBooking]);

  const handleStatusChange = useCallback(async (newStatus, override = false) => {
    if (!booking) return;
    setActionLoading(newStatus);
    try {
      const res = await apiFetch(`/bookings/${booking.id}/status`, {
        method: 'PUT',
        body: { status: newStatus, override },
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed'); }
      const data = await res.json();
      setBooking(data.booking);
      toast.success(`Booking ${newStatus.replace('_', ' ')}`);
      onUpdate?.();
    } catch (err) {
      toast.error('Failed to update status', { description: err.message });
    } finally { setActionLoading(null); }
  }, [booking, apiFetch, onUpdate]);

  const handleReschedule = useCallback(() => {
    if (!booking) return;
    createDraft({
      patientMode: 'selected', patientId: booking.patientId,
      patient: { firstName: booking.patient.firstName, lastName: booking.patient.lastName, email: booking.patient.email, phone: booking.patient.phone },
      siteKey: booking.siteKey,
      isReschedule: true,
      bookingId: booking.id,
      serviceId: booking.serviceId,
      providerId: booking.providerId,
      duration: booking.duration,
    });
    onClose();
  }, [booking, createDraft, onClose]);

  const handleRebook = useCallback(() => {
    if (!booking) return;
    createDraft({
      patientMode: 'selected', patientId: booking.patientId,
      patient: { firstName: booking.patient.firstName, lastName: booking.patient.lastName, email: booking.patient.email, phone: booking.patient.phone },
      siteKey: booking.siteKey,
    });
    onClose();
  }, [booking, createDraft, onClose]);

  const handleCopyRef = useCallback(() => {
    if (!booking) return;
    navigator.clipboard.writeText(booking.reference);
    toast.success('Reference copied');
  }, [booking]);

  // After any finance action, refetch
  const handleFinanceSuccess = useCallback(() => {
    setActiveFinanceForm(null);
    fetchBooking();
    onUpdate?.();
  }, [fetchBooking, onUpdate]);

  const dateInfo = useMemo(() => {
    if (!booking) return null;
    const dateStr = booking.confirmedDate || booking.preferredDate;
    if (!dateStr) return { label: 'Date TBD', relative: '', upcoming: false };
    const date = new Date(dateStr);
    const upcoming = !isPast(date) || isToday(date);
    let relative = '';
    if (isToday(date)) relative = 'Today';
    else if (isTomorrow(date)) relative = 'Tomorrow';
    else relative = formatDistanceToNow(date, { addSuffix: true });
    return { label: format(date, 'EEEE, d MMMM yyyy'), relative, upcoming, date };
  }, [booking]);

  const availableActions = useMemo(() => booking ? (TRANSITIONS[booking.status] || []) : [], [booking]);
  const isTerminal = availableActions.length === 0;

  // Transaction list (sorted newest first)
  const transactions = useMemo(() => {
    if (!booking?.paymentTransactions) return [];
    return [...booking.paymentTransactions].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [booking]);

  // Financial breakdown
  const financeBreakdown = useMemo(() => {
    if (!booking) return null;
    return {
      charged: booking.amountCharged ?? booking.depositAmount ?? 0,
      paid: booking.amountPaid ?? 0,
      refunded: booking.totalRefunded ?? 0,
      balance: booking.balance ?? 0,
      overpayment: booking.overpayment ?? 0,
      status: booking.computedStatus || booking.paymentStatus,
    };
  }, [booking]);
  const hasStripePayment = useMemo(() => {
    return transactions.some(t => t.stripePaymentIntentId && t.type === 'payment' && t.status === 'completed' && !t.voidedById);
  }, [transactions]);

  if (!bookingId) return null;

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed top-0 right-0 bottom-0 w-full lg:max-w-4xl md:max-w-2xl sm:max-w-xl max-w-full bg-white shadow-2xl border-l border-stone-200 z-50 flex flex-col">

        {loading || !booking ? (
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-2"><Skeleton className="h-5 w-32" /><Skeleton className="h-8 w-56" /></div>
              <Skeleton className="h-8 w-8 rounded-full" />
            </div>
            <Skeleton className="h-24 w-full rounded-xl" />
            <Skeleton className="h-36 w-full rounded-xl" />
            <Skeleton className="h-24 w-full rounded-xl" />
          </div>
        ) : (
          <>
            {/* ── Header ── */}
            <div className="px-6 pt-5 pb-4 border-b border-stone-100 shrink-0">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <button onClick={handleCopyRef} className="text-xs font-mono text-stone-400 hover:text-stone-700 transition-colors flex items-center gap-1" title="Copy reference">
                      {booking.reference} <Copy size={10} />
                    </button>
                    <span className={cn('inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border', STATUS_CONFIG[booking.status]?.bg, STATUS_CONFIG[booking.status]?.text, STATUS_CONFIG[booking.status]?.border)}>
                      <span className={cn('w-1.5 h-1.5 rounded-full', STATUS_CONFIG[booking.status]?.dot)} />
                      {STATUS_CONFIG[booking.status]?.label || booking.status}
                    </span>
                  </div>
                  <h3 className="text-xl font-playfair font-medium text-stone-900 mt-1.5 truncate">{booking.service}</h3>
                  <p className="text-xs text-stone-400 mt-0.5">Created {formatDistanceToNow(new Date(booking.createdAt), { addSuffix: true })}</p>
                </div>
                <button onClick={onClose} className="h-8 w-8 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200 transition-colors shrink-0 ml-3">
                  <X className="w-4 h-4 text-stone-500" />
                </button>
              </div>
            </div>

            {/* ── Scrollable Content ── */}
            <div className="flex-1 overflow-y-auto bg-stone-50/30 p-4 md:p-6">
              <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 items-start">
                
                {/* ── Left Column ── */}
                <div className="space-y-6">
                    {/* Pending Banner */}
                    {booking.status === 'pending' && (
                      <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/80 rounded-xl">
                        <div className="flex items-start gap-3">
                          <div className="h-9 w-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0"><AlertTriangle className="w-4.5 h-4.5 text-amber-600" /></div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-amber-900">Awaiting Confirmation</h4>
                            <p className="text-xs text-amber-700 mt-0.5">This booking needs to be confirmed or cancelled.</p>
                          </div>
                        </div>
                        <div className="mt-3 flex gap-2">
                          <Button size="sm" onClick={() => handleStatusChange('confirmed')} disabled={actionLoading === 'confirmed'} className="bg-emerald-600 text-white hover:bg-emerald-700 h-8 text-xs font-semibold flex-1">
                            <Check className="w-3.5 h-3.5 mr-1" /> {actionLoading === 'confirmed' ? 'Confirming...' : 'Confirm'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleStatusChange('cancelled')} disabled={actionLoading === 'cancelled'} className="h-8 text-xs font-semibold border-amber-300 text-amber-800 hover:bg-amber-100 bg-white/60">
                            <XCircle className="w-3.5 h-3.5 mr-1" /> Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* ── Date & Time Card ── */}
                    <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                      <div className="p-4">
                        <div className="flex items-center gap-4">
                          <div className={cn('h-14 w-14 rounded-xl flex flex-col items-center justify-center shrink-0 border', dateInfo?.upcoming ? 'bg-emerald-50 border-emerald-100' : 'bg-stone-50 border-stone-100')}>
                            <span className={cn('text-[9px] font-bold uppercase', dateInfo?.upcoming ? 'text-emerald-500' : 'text-stone-400')}>
                              {booking.confirmedDate ? format(new Date(booking.confirmedDate), 'MMM') : 'TBD'}
                            </span>
                            <span className={cn('text-xl font-bold -mt-0.5', dateInfo?.upcoming ? 'text-emerald-700' : 'text-stone-500')}>
                              {booking.confirmedDate ? format(new Date(booking.confirmedDate), 'd') : '?'}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-stone-900">{dateInfo?.label || 'Date TBD'}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-stone-500">
                              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {booking.confirmedTime || booking.preferredTime || 'Time TBD'}</span>
                              {booking.duration && <span className="flex items-center gap-1"><Timer className="w-3.5 h-3.5" /> {booking.duration} min</span>}
                            </div>
                            {dateInfo?.relative && (
                              <span className={cn('inline-flex items-center mt-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full', isToday(dateInfo.date) ? 'bg-emerald-100 text-emerald-700' : dateInfo.upcoming ? 'bg-blue-50 text-blue-600' : 'bg-stone-100 text-stone-500')}>
                                {dateInfo.relative}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="bg-stone-50/70 px-4 py-3 border-t border-stone-100 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="text-stone-400 flex items-center gap-1.5"><MapPin className="w-3 h-3" /> Site</span>
                          <span className="font-medium text-stone-700 text-right">{SITE_NAMES[booking.siteKey] || booking.siteName}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-stone-400 flex items-center gap-1.5"><Stethoscope className="w-3 h-3" /> Provider</span>
                          <span className="font-medium text-stone-700 text-right">{booking.provider?.name || 'Unassigned'}</span>
                        </div>
                      </div>
                    </div>


                    {/* ── Notes ── */}
                    {(booking.message || booking.adminNotes) && (
                      <div>
                        <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-stone-400 mb-2">Notes</h4>
                        <div className="space-y-2">
                          {booking.message && <div className="bg-stone-50 border border-stone-200 rounded-xl p-3.5"><p className="text-[10px] font-bold uppercase tracking-wider text-stone-400 mb-1">Patient Message</p><p className="text-sm text-stone-700 leading-relaxed">{booking.message}</p></div>}
                          {booking.adminNotes && <div className="bg-blue-50/60 border border-blue-200/60 rounded-xl p-3.5"><p className="text-[10px] font-bold uppercase tracking-wider text-blue-400 mb-1">Internal Notes</p><p className="text-sm text-blue-900 leading-relaxed">{booking.adminNotes}</p></div>}
                        </div>
                      </div>
                    )}

                    {/* ── Manage Booking ── */}
                    <div>
                      <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-stone-400 mb-2">Manage Booking</h4>
                      <div className="bg-white border border-stone-200 rounded-xl p-4 space-y-4">
                        <div>
                          <label className="text-[10px] font-medium text-stone-600 mb-1.5 flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Edit Service</label>
                          <select 
                            value={booking.serviceId || ''} 
                            onChange={handleServiceChange}
                            disabled={actionLoading === 'service'}
                            className="w-full h-9 rounded-lg border border-stone-200 text-sm px-3 bg-stone-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          >
                            <option value={booking.serviceId} disabled>{booking.service}</option>
                            {services.map(s => (
                              <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] font-medium text-stone-600 mb-1.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Override Status</label>
                          <select 
                            value={booking.status}
                            onChange={(e) => handleStatusChange(e.target.value, true)}
                            disabled={!!actionLoading}
                            className="w-full h-9 rounded-lg border border-stone-200 text-sm px-3 bg-stone-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          >
                            {Object.keys(STATUS_CONFIG).map(st => (
                              <option key={st} value={st}>{STATUS_CONFIG[st].label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  {/* ── Other Actions ── */}
                  {booking.status === 'confirmed' && (
                    <div>
                      <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-stone-400 mb-2">Other Actions</h4>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={() => handleStatusChange('no_show')} disabled={!!actionLoading} className="flex-1 h-10 text-xs font-semibold text-red-600 border-red-200 hover:bg-red-50"><Ban className="w-3.5 h-3.5 mr-1.5" />No Show</Button>
                        <Button variant="outline" onClick={() => handleStatusChange('cancelled')} disabled={!!actionLoading} className="flex-1 h-10 text-xs font-semibold text-stone-500 border-stone-200 hover:bg-stone-100"><XCircle className="w-3.5 h-3.5 mr-1.5" />Cancel</Button>
                      </div>
                    </div>
                  )}

                </div>

                {/* ── Right Column ── */}
                <div className="space-y-6">
                    {/* ── Patient Card ── */}
                    <div>
                      <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-stone-400 mb-2">Patient</h4>
                      <div className="bg-white border border-stone-200 rounded-xl p-4">
                        <div className="flex items-center gap-3">
                          <div className="h-11 w-11 rounded-full bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center shrink-0">
                            <span className="text-sm font-bold text-stone-600">{booking.patient.firstName?.[0]}{booking.patient.lastName?.[0]}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <Link to={`/admin/patients/${booking.patientId}`} className="text-sm font-semibold text-stone-900 hover:text-blue-700 transition-colors" onClick={() => onClose()}>
                              {booking.patient.firstName} {booking.patient.lastName}
                            </Link>
                            <p className="text-xs text-stone-400 capitalize">{booking.audience}</p>
                          </div>
                          <Link to={`/admin/patients/${booking.patientId}`} className="h-8 w-8 rounded-lg bg-stone-100 hover:bg-stone-200 flex items-center justify-center transition-colors" onClick={() => onClose()}>
                            <ExternalLink className="w-3.5 h-3.5 text-stone-500" />
                          </Link>
                        </div>
                        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-stone-100">
                          {booking.patient.phone && <a href={`tel:${booking.patient.phone}`} className="flex items-center gap-1.5 text-xs text-stone-600 bg-stone-50 hover:bg-stone-100 px-3 py-1.5 rounded-lg transition-colors border border-stone-100"><Phone className="w-3 h-3 text-stone-400" />{booking.patient.phone}</a>}
                          {booking.patient.email && <a href={`mailto:${booking.patient.email}`} className="flex items-center gap-1.5 text-xs text-stone-600 bg-stone-50 hover:bg-stone-100 px-3 py-1.5 rounded-lg transition-colors border border-stone-100 truncate"><Mail className="w-3 h-3 text-stone-400 shrink-0" /><span className="truncate">{booking.patient.email}</span></a>}
                        </div>
                        <div className="flex gap-1.5 mt-2.5">
                          {booking.patient.phone && <a href={`https://wa.me/${booking.patient.phone?.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[11px] text-stone-500 hover:text-emerald-700 hover:bg-emerald-50 px-2.5 py-1 rounded-md transition-colors"><MessageCircle className="w-3 h-3" /> WhatsApp</a>}
                          <button onClick={handleRebook} className="flex items-center gap-1.5 text-[11px] text-stone-500 hover:text-blue-700 hover:bg-blue-50 px-2.5 py-1 rounded-md transition-colors"><CalendarPlus className="w-3 h-3" /> Rebook</button>
                          <button onClick={() => toast.success('Medical form link sent')} className="flex items-center gap-1.5 text-[11px] text-stone-500 hover:text-violet-700 hover:bg-violet-50 px-2.5 py-1 rounded-md transition-colors"><FileText className="w-3 h-3" /> Medical Form</button>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-stone-400 mb-2">Financial Health</h4>
                      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">

                        {/* Balance Breakdown */}
                        {financeBreakdown && (
                          <div className="p-4 space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-stone-500">Charged</span>
                              <span className="font-bold text-stone-900">£{financeBreakdown.charged.toFixed(2)}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-stone-500">Paid</span>
                              <span className="font-semibold text-emerald-700">£{financeBreakdown.paid.toFixed(2)}</span>
                            </div>
                            {financeBreakdown.refunded > 0 && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-stone-500">Refunded</span>
                                <span className="font-semibold text-red-600">-£{financeBreakdown.refunded.toFixed(2)}</span>
                              </div>
                            )}
                            <div className="border-t border-dashed border-stone-200 pt-2 mt-2 flex items-center justify-between text-sm">
                              <span className="font-medium text-stone-700">Balance</span>
                              <div className="flex items-center gap-2">
                                <span className={cn('font-bold text-lg', financeBreakdown.balance > 0 ? 'text-amber-600' : 'text-emerald-700')}>
                                  £{financeBreakdown.balance.toFixed(2)}
                                </span>
                                <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border',
                                  PAYMENT_CONFIG[financeBreakdown.status]?.bg, PAYMENT_CONFIG[financeBreakdown.status]?.text, PAYMENT_CONFIG[financeBreakdown.status]?.border)}>
                                  {PAYMENT_CONFIG[financeBreakdown.status]?.label || financeBreakdown.status}
                                </span>
                              </div>
                            </div>
                            <div className="h-1.5 w-full bg-stone-100 rounded-full mt-3 overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(100, Math.max(0, financeBreakdown.charged > 0 ? (financeBreakdown.paid / financeBreakdown.charged) * 100 : 0))}%` }} /></div>
                            {financeBreakdown.overpayment > 0 && (
                              <div className="mt-2 p-2.5 bg-violet-50 rounded-lg border border-violet-100 text-xs text-violet-700 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  <Gift className="w-3.5 h-3.5 shrink-0" />
                                  <span>Patient overpaid by <strong>£{financeBreakdown.overpayment.toFixed(2)}</strong></span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Patient Credit Hint */}
                        {booking.patient?.creditBalancePence > 0 && financeBreakdown?.balance > 0 && (
                          <div className="px-4 pb-3">
                            <div className="p-2.5 bg-blue-50 rounded-lg border border-blue-100 text-xs text-blue-700 flex items-center justify-between">
                              <span className="flex items-center gap-2"><Gift className="w-3.5 h-3.5" /> Patient has <strong>£{(booking.patient.creditBalancePence / 100).toFixed(2)}</strong> credit</span>
                              <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-blue-200 text-blue-700 hover:bg-blue-100"
                                onClick={async () => {
                                  try {
                                    const amt = Math.min(booking.patient.creditBalancePence / 100, financeBreakdown.balance);
                                    const res = await apiFetch('/payments/apply-credit', { method: 'POST', body: { patientId: booking.patientId, bookingId: booking.id, amount: amt } });
                                    if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
                                    toast.success(`£${amt.toFixed(2)} credit applied`);
                                    handleFinanceSuccess();
                                  } catch (err) { toast.error(err.message); }
                                }}>Apply</Button>
                            </div>
                          </div>
                        )}

                        {/* Transaction History */}
                        {transactions.length > 0 && (
                          <div className="border-t border-stone-100">
                            <button onClick={() => setShowTransactions(!showTransactions)}
                              className="w-full px-4 py-2.5 flex items-center justify-between text-xs font-semibold text-stone-500 hover:bg-stone-50 transition-colors">
                              <span>Transaction History ({transactions.length})</span>
                              <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', showTransactions && 'rotate-180')} />
                            </button>
                            {showTransactions && (
                              <div className="px-4 pb-3 space-y-1.5">
                                {transactions.map(tx => {
                                  const cfg = TX_TYPE_CONFIG[tx.type] || TX_TYPE_CONFIG.payment;
                                  const Icon = cfg.icon;
                                  const isVoided = !!tx.voidedById || tx.status === 'voided';
                                  const method = tx.metadata?.method || (tx.stripePaymentIntentId ? 'stripe' : tx.type);

                                  return (
                                    <button key={tx.id} onClick={() => setSelectedTx(tx)} className={cn('w-full text-left flex items-start gap-2.5 py-2 px-2.5 rounded-lg border border-transparent hover:border-stone-100 hover:bg-stone-50/50 transition-colors', isVoided && 'opacity-50')}>
                                      <div className={cn('w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5', cfg.bg)}>
                                        <Icon className={cn('w-3.5 h-3.5', cfg.color)} />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between">
                                          <span className={cn('text-xs font-semibold', isVoided ? 'line-through text-stone-400' : 'text-stone-800')}>
                                            {tx.type === 'refund' ? '-' : ''}£{tx.amount?.toFixed(2)}
                                          </span>
                                          <span className="text-[10px] text-stone-400">{formatDistanceToNow(new Date(tx.createdAt), { addSuffix: true })}</span>
                                        </div>
                                        <div className="flex items-center gap-2 mt-0.5">
                                          <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded', cfg.bg, cfg.color)}>{cfg.label}</span>
                                          <span className="text-[10px] text-stone-400 capitalize">{method}</span>
                                          {isVoided && <span className="text-[10px] font-medium text-red-500 bg-red-50 px-1.5 py-0.5 rounded">Voided</span>}
                                        </div>
                                        {tx.reason && <p className="text-[10px] text-stone-400 mt-0.5 truncate">Reason: {tx.reason}</p>}
                                        {tx.recordedBy && <p className="text-[10px] text-stone-500 mt-0.5">Recorded by: <span className="font-medium text-stone-700">{tx.recordedBy}</span></p>}
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Finance Action Buttons */}
                        <div className="border-t border-stone-100 p-3">
                          <div className="flex flex-wrap gap-1.5">
                            {financeBreakdown?.balance > 0 && (
                              <>
                                <button onClick={() => setActiveFinanceForm(activeFinanceForm === 'payment' ? null : 'payment')}
                                  className={cn('flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-lg border transition-colors', activeFinanceForm === 'payment' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'border-stone-200 text-stone-600 hover:bg-stone-50')}>
                                  <Banknote className="w-3 h-3" /> Record Payment
                                </button>
                                <button onClick={async () => {
                                  try {
                                    const res = await apiFetch('/payments/create-session', { method: 'POST', body: { bookingId: booking.id } });
                                    if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
                                    const { url } = await res.json();
                                    if (url) window.open(url, '_blank');
                                    toast.success('Payment link created');
                                  } catch (err) { toast.error(err.message); }
                                }} className="flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-lg border border-stone-200 text-stone-600 hover:bg-stone-50 transition-colors">
                                  <Send className="w-3 h-3" /> Send Link
                                </button>
                              </>
                            )}
                            {(financeBreakdown?.paid > 0 || financeBreakdown?.overpayment > 0) && (
                              <button onClick={() => setActiveFinanceForm(activeFinanceForm === 'refund' ? null : 'refund')}
                                className={cn('flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-lg border transition-colors', activeFinanceForm === 'refund' ? 'bg-red-50 border-red-200 text-red-700' : 'border-stone-200 text-stone-600 hover:bg-stone-50')}>
                                <ArrowUpRight className="w-3 h-3" /> Issue Refund
                              </button>
                            )}
                            <button onClick={() => setActiveFinanceForm(activeFinanceForm === 'charge' ? null : 'charge')}
                              className={cn('flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-lg border transition-colors', activeFinanceForm === 'charge' ? 'bg-blue-50 border-blue-200 text-blue-700' : 'border-stone-200 text-stone-600 hover:bg-stone-50')}>
                              <Receipt className="w-3 h-3" /> Adjust Charge
                            </button>
                          </div>

                          {/* Inline Forms */}
                          {activeFinanceForm === 'payment' && (
                            <RecordPaymentForm booking={booking} apiFetch={apiFetch} onSuccess={handleFinanceSuccess} onCancel={() => setActiveFinanceForm(null)} />
                          )}
                          {activeFinanceForm === 'refund' && (
                            <RefundForm booking={booking} apiFetch={apiFetch} hasStripe={hasStripePayment} onSuccess={handleFinanceSuccess} onCancel={() => setActiveFinanceForm(null)} />
                          )}
                          {activeFinanceForm === 'charge' && (
                            <AdjustChargeForm booking={booking} apiFetch={apiFetch} onSuccess={handleFinanceSuccess} onCancel={() => setActiveFinanceForm(null)} />
                          )}
                        </div>
                      </div>
                    </div>
                </div>
              </div>
            </div>

            {/* ── Sticky Footer Actions ── */}
            <div className="shrink-0 px-6 py-4 border-t border-stone-100 bg-stone-50/50">
              {isTerminal ? (
                <div className="flex gap-2">
                  <Button onClick={() => handleStatusChange('confirmed', true)} disabled={!!actionLoading} variant="outline" className="flex-1 border-stone-200 text-stone-500 hover:bg-stone-100 h-10 text-xs font-semibold"><Undo2 className="w-3.5 h-3.5 mr-1.5" /> Undo {STATUS_CONFIG[booking.status]?.label}</Button>
                  <Button onClick={handleReschedule} variant="outline" className="flex-1 bg-stone-50 text-stone-700 hover:bg-stone-100 border-stone-200 h-10 text-xs font-semibold"><CalendarIcon className="w-3.5 h-3.5 mr-1.5" /> Reschedule</Button>
                  <Button onClick={handleRebook} className="flex-1 bg-stone-900 text-white hover:bg-stone-800 h-10 text-xs font-semibold"><CalendarPlus className="w-3.5 h-3.5 mr-1.5" /> Rebook</Button>
                  <Button variant="outline" onClick={() => { navigate(`/admin/patients/${booking.patientId}`); onClose(); }} className="h-10 w-10 p-0 text-stone-500 border-stone-200"><User className="w-4 h-4" /></Button>
                </div>
              ) : (
                <div className="flex gap-2 flex-wrap">
                  {booking.status === 'confirmed' && (
                    <>
                      <Button onClick={() => handleStatusChange('completed')} disabled={!!actionLoading} className="flex-1 bg-blue-600 text-white hover:bg-blue-700 h-10 text-xs font-semibold"><Check className="w-3.5 h-3.5 mr-1.5" />{actionLoading === 'completed' ? 'Completing...' : 'Mark Completed'}</Button>
                      <Button onClick={handleReschedule} variant="outline" className="flex-1 bg-stone-50 text-stone-700 hover:bg-stone-100 border-stone-200 h-10 text-xs font-semibold"><CalendarIcon className="w-3.5 h-3.5 mr-1.5" /> Reschedule</Button>
                    </>
                  )}
                  {booking.status === 'pending' && (
                    <>
                      <Button onClick={() => handleStatusChange('confirmed')} disabled={!!actionLoading} className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700 h-10 text-xs font-semibold"><Check className="w-3.5 h-3.5 mr-1.5" />{actionLoading === 'confirmed' ? 'Confirming...' : 'Confirm Booking'}</Button>
                      <Button onClick={handleReschedule} variant="outline" className="flex-1 bg-stone-50 text-stone-700 hover:bg-stone-100 border-stone-200 h-10 text-xs font-semibold"><CalendarIcon className="w-3.5 h-3.5 mr-1.5" /> Reschedule</Button>
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </motion.div>

      <AnimatePresence>
        {selectedTx && (
          <TransactionDetailPanel
            tx={selectedTx}
            apiFetch={apiFetch}
            onClose={() => setSelectedTx(null)}
            onUpdate={() => { setSelectedTx(null); fetchBooking(); }}
            navigate={navigate}
          />
        )}
      </AnimatePresence>
    </>
  );
}

// ── Record Payment Inline Form ──────────────────────────────────────────────

function RecordPaymentForm({ booking, apiFetch, onSuccess, onCancel }) {
  const [amount, setAmount] = useState(String(booking.balance || 0));
  const [method, setMethod] = useState('cash');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await apiFetch('/payments/manual', { method: 'POST', body: { bookingId: booking.id, amount: parseFloat(amount), method, notes } });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed'); }
      toast.success(`£${parseFloat(amount).toFixed(2)} payment recorded`);
      onSuccess();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-3 p-3 bg-emerald-50/50 rounded-lg border border-emerald-100 space-y-2.5">
      <div className="grid grid-cols-2 gap-2">
        <div><label className="text-[10px] font-medium text-stone-600 mb-1 block">Amount (£)</label><Input type="number" step="0.01" min="0.01" required value={amount} onChange={e => setAmount(e.target.value)} className="h-8 text-xs bg-white" /></div>
        <div><label className="text-[10px] font-medium text-stone-600 mb-1 block">Method</label><select value={method} onChange={e => setMethod(e.target.value)} className="w-full h-8 rounded-md border border-stone-200 text-xs px-2 bg-white"><option value="cash">Cash</option><option value="card">Card (Manual)</option><option value="bank_transfer">Bank Transfer</option><option value="other">Other</option></select></div>
      </div>
      <div><label className="text-[10px] font-medium text-stone-600 mb-1 block">Notes</label><textarea value={notes} onChange={e => setNotes(e.target.value)} rows={1} className="w-full rounded-md border border-stone-200 text-xs px-2 py-1.5 resize-none bg-white" placeholder="e.g. Paid cash at reception" /></div>
      <div className="flex gap-2 pt-1"><Button type="submit" disabled={saving} size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white">{saving ? 'Recording...' : 'Confirm'}</Button><Button type="button" variant="ghost" size="sm" onClick={onCancel} className="h-7 text-xs">Cancel</Button></div>
    </form>
  );
}

// ── Refund Inline Form ──────────────────────────────────────────────────────

function RefundForm({ booking, apiFetch, hasStripe, onSuccess, onCancel }) {
  const [amount, setAmount] = useState(String(booking.amountPaid || 0));
  const [method, setMethod] = useState(hasStripe ? 'stripe' : 'manual');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { toast.error('Reason is required'); return; }
    setSaving(true);
    try {
      const res = await apiFetch('/payments/refund', { method: 'POST', body: { bookingId: booking.id, amount: parseFloat(amount), method, reason } });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed'); }
      const data = await res.json();
      toast.success(`£${parseFloat(amount).toFixed(2)} refunded${data.creditApplied ? ' (as credit)' : ''}`);
      onSuccess();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-3 p-3 bg-red-50/50 rounded-lg border border-red-100 space-y-2.5">
      <div className="grid grid-cols-2 gap-2">
        <div><label className="text-[10px] font-medium text-stone-600 mb-1 block">Amount (£)</label><Input type="number" step="0.01" min="0.01" max={booking.amountPaid} required value={amount} onChange={e => setAmount(e.target.value)} className="h-8 text-xs bg-white" /></div>
        <div><label className="text-[10px] font-medium text-stone-600 mb-1 block">Method</label><select value={method} onChange={e => setMethod(e.target.value)} className="w-full h-8 rounded-md border border-stone-200 text-xs px-2 bg-white">{hasStripe && <option value="stripe">Stripe Refund</option>}<option value="manual">Manual Refund</option><option value="credit">→ Patient Credit</option></select></div>
      </div>
      <div><label className="text-[10px] font-medium text-stone-600 mb-1 block">Reason <span className="text-red-500">*</span></label><textarea value={reason} onChange={e => setReason(e.target.value)} rows={1} required className="w-full rounded-md border border-stone-200 text-xs px-2 py-1.5 resize-none bg-white" placeholder="Why is this refund being issued?" /></div>
      {method === 'credit' && <p className="text-[10px] text-violet-600 bg-violet-50 px-2 py-1 rounded">This will add £{parseFloat(amount || 0).toFixed(2)} to the patient's credit balance</p>}
      <div className="flex gap-2 pt-1"><Button type="submit" disabled={saving} size="sm" className="h-7 text-xs bg-red-600 hover:bg-red-700 text-white">{saving ? 'Processing...' : 'Issue Refund'}</Button><Button type="button" variant="ghost" size="sm" onClick={onCancel} className="h-7 text-xs">Cancel</Button></div>
    </form>
  );
}

// ── Adjust Charge Inline Form ───────────────────────────────────────────────

function AdjustChargeForm({ booking, apiFetch, onSuccess, onCancel }) {
  const currentCharge = booking.amountCharged ?? booking.depositAmount ?? 0;
  const [newCharge, setNewCharge] = useState(String(currentCharge));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const parsedCharge = parseFloat(newCharge) || 0;
  const isBelowPaid = parsedCharge < (booking.amountPaid || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { toast.error('Reason is required'); return; }
    setSaving(true);
    try {
      const res = await apiFetch('/payments/charge', { method: 'POST', body: { bookingId: booking.id, amountCharged: parsedCharge, reason } });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed'); }
      toast.success(`Charge updated to £${parsedCharge.toFixed(2)}`);
      onSuccess();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-3 p-3 bg-blue-50/50 rounded-lg border border-blue-100 space-y-2.5">
      <div><label className="text-[10px] font-medium text-stone-600 mb-1 block">New Charge Amount (£)</label><Input type="number" step="0.01" min="0" required value={newCharge} onChange={e => setNewCharge(e.target.value)} className="h-8 text-xs bg-white" /></div>
      {isBelowPaid && <p className="text-[10px] text-violet-600 bg-violet-50 border border-violet-100 px-2 py-1 rounded flex items-center gap-1.5"><Gift className="w-3.5 h-3.5" />Setting charge below amount paid will automatically issue £{((booking.amountPaid || 0) - parsedCharge).toFixed(2)} to patient credit.</p>}
      <div><label className="text-[10px] font-medium text-stone-600 mb-1 block">Reason <span className="text-red-500">*</span></label><textarea value={reason} onChange={e => setReason(e.target.value)} rows={1} required className="w-full rounded-md border border-stone-200 text-xs px-2 py-1.5 resize-none bg-white" placeholder="e.g. Treatment plan changed" /></div>
      <div className="flex gap-2 pt-1"><Button type="submit" disabled={saving} size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white">{saving ? 'Saving...' : 'Update Charge'}</Button><Button type="button" variant="ghost" size="sm" onClick={onCancel} className="h-7 text-xs">Cancel</Button></div>
    </form>
  );
}
